/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack, ListItem, Paper, Typography, Box, styled } from '@mui/material';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React, { useRef } from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import {
  CustomToggleButton,
  CustomTooltip,
  dateUtils,
} from '@opengeoweb/shared';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import type { Area } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';

const RowComponent = ({
  index,
  style,
  items,
  selectedAreas,
  onAddArea,
  onRemoveArea,
  onHoverDrawing,
  onHoverDrawingEnd,
  isChangeAreaDisabled,
  shouldDisableRemoveWarning,
  t,
}: RowComponentProps<{
  items: DrawingListItem[];
  selectedAreas: Area[];
  onAddArea: (drawingListItem: DrawingListItem) => void;
  onRemoveArea: (drawingListItem: DrawingListItem) => void;
  onHoverDrawing: (drawingListItem: DrawingListItem) => void;
  onHoverDrawingEnd: () => void;
  isChangeAreaDisabled: boolean;
  shouldDisableRemoveWarning?: boolean;
  t: TFunction;
}>): React.ReactElement => {
  const listItem = items[index];
  if (typeof listItem === 'string') {
    return (
      <Typography
        key={index}
        style={style}
        sx={{
          paddingLeft: 2.5,
          paddingBottom: 0.5,
          paddingTop: 1,
          opacity: 0.67,
          lineHeight: '16px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        variant="caption"
      >
        {listItem}
      </Typography>
    );
  }

  return (
    <ContainerDiv>
      <ListItem
        data-testid={`drawing-row-${listItem.id}`}
        disablePadding
        key={listItem.id}
        onMouseEnter={(): void => onHoverDrawing(listItem)}
        onMouseLeave={(): void => onHoverDrawingEnd()}
        secondaryAction={
          <ListItemAction
            isDisabled={isChangeAreaDisabled}
            listItem={listItem}
            onAddArea={onAddArea}
            onRemoveArea={onRemoveArea}
            selectedAreas={selectedAreas}
            shouldDisableRemoveWarning={shouldDisableRemoveWarning}
            t={t}
          />
        }
        style={style}
        sx={{
          '&:hover .MuiPaper-root': {
            backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            margin: 0.25,
            backgroundColor: 'geowebColors.background.surface',
            border: 'solid 1px',
            borderColor: 'geowebColors.cards.cardContainerBorder',
          }}
        >
          <Stack>
            <Typography
              sx={{
                lineHeight: '16px',
                maxHeight: '32px',
                overflow: 'hidden',
              }}
              variant="caption"
            >
              {listItem.objectName}
            </Typography>
            {!isArea(listItem) && (
              <Typography
                sx={{
                  opacity: 0.67,
                  lineHeight: '16px',
                  maxHeight: '32px',
                  overflow: 'hidden',
                }}
                variant="caption"
              >
                {getDisplayDate(listItem.lastUpdatedTime)}
              </Typography>
            )}
          </Stack>
        </Paper>
      </ListItem>
    </ContainerDiv>
  );
};

const DRAWING_ITEM_SIZE = 44;
const DRAWING_ITEM_SIZE_SMALL_WIDTH = 72;
const HEADER_ITEM_SIZE = 28;
const BREAKPOINT = 320;
const ERROR_HEIGHT = 48;

interface DrawingAreaList {
  title: string;
  items: DrawingListItem[];
}

export const useGetFilterHeight = (
  filterRef?: React.RefObject<HTMLElement>,
): number => {
  const [filterHeight, setFilterHeight] = React.useState(0);

  React.useEffect(() => {
    let resizeObserver: ResizeObserver;
    if (filterRef) {
      resizeObserver = new ResizeObserver((entry) => {
        const filterHeight = entry[0].target.getBoundingClientRect().height;
        setFilterHeight(filterHeight);
      });

      resizeObserver.observe(filterRef.current);
    }
    return (): void => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [filterRef]);

  return filterHeight;
};

const isArea = (item: DrawingListItem): boolean =>
  !item.keywords?.includes('Objects');

export const getListInclHeaders = (
  items: DrawingListItem[],
  translateFn: TFunction,
): { drawings: DrawingAreaList; areas: DrawingAreaList } => {
  const { drawings, areas } = items.reduce<{
    areas: DrawingListItem[];
    drawings: DrawingListItem[];
  }>(
    ({ drawings, areas }, item) => {
      const isDrawing = !isArea(item);
      if (isDrawing) {
        return { areas, drawings: drawings.concat(item) };
      }
      return { areas: areas.concat(item), drawings };
    },
    {
      drawings: [],
      areas: [],
    },
  );

  return {
    drawings: {
      title: `${drawings.length} ${translateFn('drawings')}`,
      items: drawings,
    },
    areas: {
      title: `${areas.length} ${translateFn('areas')}`,
      items: areas,
    },
  };
};

export const getLabel = (
  selectedAreas: Area[],
  isAreaSelected: boolean,
): string => {
  if (isAreaSelected) {
    return 'remove-button';
  }
  return selectedAreas.length === 0 ? 'add-button' : 'replace-button';
};

export const getDisplayDate = (date: string): string =>
  `${dateUtils.dateToString(
    dateUtils.utc(date),
    dateUtils.DATE_FORMAT_NAME_OF_DAY,
  )} UTC`;

const ListItemAction = ({
  listItem,
  selectedAreas,
  onAddArea,
  onRemoveArea,
  isDisabled,
  t,
  shouldDisableRemoveWarning = false,
}: {
  listItem: DrawingListItem;
  selectedAreas: Area[];
  onAddArea: (drawingListItem: DrawingListItem) => void;
  onRemoveArea: (drawingListItem: DrawingListItem) => void;
  isDisabled: boolean;
  t: TFunction;
  shouldDisableRemoveWarning?: boolean;
}): React.ReactNode => {
  const isAreaSelected = !!selectedAreas.find(
    (area) => area.objectName === listItem.objectName,
  );
  const label = t(getLabel(selectedAreas, isAreaSelected));

  const onClick = (): void => {
    isAreaSelected ? onRemoveArea(listItem) : onAddArea(listItem);
  };

  const shouldDisableRemove = shouldDisableRemoveWarning && isAreaSelected;

  const render = (): React.ReactNode => (
    <CustomToggleButton
      disabled={isDisabled || shouldDisableRemove}
      onClick={onClick}
      selected={isAreaSelected}
      sx={{
        fontSize: '12px',
        height: '22px!important',
        minWidth: '60px',
      }}
      variant="tool"
    >
      {label}
    </CustomToggleButton>
  );

  if (shouldDisableRemove) {
    return (
      <CustomTooltip placement="top" title={t('disabled-button-tooltip')}>
        <span>{render()}</span>
      </CustomTooltip>
    );
  }

  return render();
};

const ContainerDiv = styled('div')(() => ({
  '& .MuiListItem-root': {
    paddingBottom: '2px',
  },
  '& .MuiPaper-root': {
    height: '100%',
    display: 'grid',
  },
  '& .MuiListItemSecondaryAction-root': {
    right: '12px',
  },
  '& .MuiStack-root': {
    paddingRight: '68px',
    width: '100%',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
    justifyContent: 'center',
    paddingLeft: '4px',
  },
}));

export const AreasDrawings: FC<{
  drawingListItems: DrawingListItem[];
  isLoading?: boolean;
  hasError?: boolean;
  filterRef?: React.RefObject<HTMLElement>;
  onAddArea?: (drawingListItem: DrawingListItem) => void;
  onRemoveArea?: (drawingListItem: DrawingListItem) => void;
  onHoverDrawing?: (drawingListItem: DrawingListItem) => void;
  onHoverDrawingEnd?: () => void;
  selectedAreas?: Area[];
  isChangeAreaDisabled?: boolean;
  shouldDisableRemoveWarning?: boolean;
}> = ({
  drawingListItems,
  isLoading = false,
  hasError = false,
  filterRef,
  onAddArea = (): void => {},
  onRemoveArea = (): void => {},
  onHoverDrawing = (): void => {},
  onHoverDrawingEnd = (): void => {},
  selectedAreas = [],
  isChangeAreaDisabled = true,
  shouldDisableRemoveWarning = false,
}) => {
  const { t } = useWarningsTranslation();
  const { drawings, areas } = getListInclHeaders(drawingListItems, t);
  const filterHeight = useGetFilterHeight(filterRef);

  const items = [
    drawings.title,
    ...drawings.items,
    areas.title,
    ...areas.items,
  ];
  const getItemSize = (width: number, index: number): number => {
    const item = items[index];
    if (typeof item !== 'string') {
      return width < BREAKPOINT
        ? DRAWING_ITEM_SIZE_SMALL_WIDTH
        : DRAWING_ITEM_SIZE;
    }
    return HEADER_ITEM_SIZE;
  };

  const totalContainerHeight = hasError
    ? filterHeight + ERROR_HEIGHT
    : filterHeight;

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    setWidth(ref.current?.clientWidth || 0);
  }, [ref]);

  return (
    <Box ref={ref} sx={{ height: `calc(100% - ${totalContainerHeight}px)` }}>
      {!isLoading && drawingListItems.length === 0 ? (
        <Typography
          sx={{ paddingLeft: '16px', paddingTop: '8px' }}
          variant="body1"
        >
          {t('area-drawing-loader-no-areas-drawings')}
        </Typography>
      ) : (
        <List
          rowComponent={RowComponent}
          rowCount={items.length}
          rowHeight={(index) => getItemSize(width, index)}
          rowProps={{
            items: items as DrawingListItem[],
            selectedAreas,
            onAddArea,
            onRemoveArea,
            onHoverDrawing,
            onHoverDrawingEnd,
            isChangeAreaDisabled,
            shouldDisableRemoveWarning,
            t,
          }}
          style={{
            height: '100%',
            width: '100%',
          }}
        />
      )}
    </Box>
  );
};
